import { useState, useEffect, useMemo } from 'react';

import { Save, RotateCcw, Check, X, RefreshCw, AlertTriangle } from 'lucide-react';

import { FormField, FormInput, FormSelect } from '@/components/form';
import { EmailConnectorsPanel } from '@/components/settings/EmailConnectorsPanel';
import {
  DefaultFiltersEditor,
  TagDefinitionsEditor,
} from '@/components/settings/SalesConsolePanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useDealerKitStockSyncState, useSyncDealerKitStock } from '@/hooks/useDealerKit';
import { useEffectiveOutcomes } from '@/hooks/useOutcomes';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSettings, useBulkUpdateSettings, useEncryptionStatus } from '@/hooks/useSettings';
import { cn, extractErrorMessage } from '@/lib/utils';
import { useFeaturesStore } from '@/stores/features.store';

import { CONFIG_REGISTRY, ConfigType, SettingCategory } from '@repo/shared-types';

import type { Setting, ConfigKey } from '@repo/shared-types';

interface SettingsByCategory {
  [category: string]: Setting[];
}

const categoryLabels: Record<string, string> = {
  [SettingCategory.General]: 'General',
  [SettingCategory.Features]: 'Features',
  [SettingCategory.Branding]: 'Branding',
  [SettingCategory.Email]: 'Email',
  [SettingCategory.Twilio]: 'Twilio',
  [SettingCategory.Telnyx]: 'Telnyx',
  [SettingCategory.Call]: 'Call Settings',
  [SettingCategory.API]: 'API Keys',
  [SettingCategory.VoiceAgent]: 'Voice Agent',
  [SettingCategory.Payments]: 'Payments',
  [SettingCategory.DigitalSign]: 'Digital Signatures',
  [SettingCategory.DealerKit]: 'DealerKit',
  [SettingCategory.Campaigns]: 'Campaigns',
  [SettingCategory.SalesConsole]: 'Sales Console',
  [SettingCategory.AgentImprovement]: 'Agent Improvement',
};

// Static fallback for retry triggers while the dynamic definitions load.
const RETRYABLE_OUTCOME_FALLBACK = ['NO_ANSWER', 'BUSY', 'VOICEMAIL', 'CANCELED', 'FAILED'];

const categoryDescriptions: Record<string, string> = {
  branding: 'Configure company branding, logos, and theme colors.',
  features: 'Enable or disable platform features.',
  email: 'Email provider and notification settings.',
  twilio: 'Twilio phone number and SMS configuration.',
  telnyx: 'Telnyx phone number and connection configuration.',
  call: 'AI call behavior and voice settings.',
  api: 'Third-party API keys and integrations (OpenAI, Gemini).',
  general: 'General platform settings.',
  voice_agent: 'Voice activity detection, thinking, transcription, and audio model settings.',
  payments: 'NatWest payment link credentials and reservation fee settings.',
  digital_sign: 'Documenso e-signature instance this platform sends documents through.',
  dealer_kit: 'DealerKit integration — API credentials and stock sync settings.',
  campaigns: 'Default retry behavior applied to new campaigns (editable per campaign).',
  sales_console:
    'Contact tags (colors & types), default console filters, and operator takeover/handback messages.',
  agent_improvement:
    'Turn the agent improvement harness on or off. Which configurations it focuses on is set per configuration.',
};

// Helper to determine email provider for a setting key
const getEmailProviderForKey = (key: string): string | null => {
  if (key.startsWith('RESEND_')) return 'resend';
  if (key.startsWith('SMTP_')) return 'nodemailer';
  if (key.startsWith('SENDGRID_')) return 'sendgrid';
  if (key.startsWith('AZURE_')) return 'azure';
  return null;
};

const DealerKitStockSyncPanel = () => {
  const { toast } = useToast();
  const { data: syncState } = useDealerKitStockSyncState();
  const syncMutation = useSyncDealerKitStock();

  const isRunning = !!syncState?.running || syncMutation.isPending;

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync();
      toast({
        variant: result.started ? 'success' : 'default',
        title: result.started ? 'Stock sync started' : 'Sync already running',
        description: result.message,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Stock sync failed to start',
        description: extractErrorMessage(error),
      });
    }
  };

  const lastSynced = syncState?.last_synced_at
    ? new Date(syncState.last_synced_at).toLocaleString('en-GB')
    : 'Never';

  return (
    <div className="mt-2 rounded-md border p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <p className="text-sm font-medium">Stock mirror</p>
          <p className="text-muted-foreground text-xs">
            Pull the full DealerKit stock list to seed or reconcile the local mirror used for
            reservations. Runs in the background; the webhook keeps it up to date in real time
            between syncs.
          </p>
          <p className="text-muted-foreground text-xs">
            {isRunning ? (
              <span className="text-foreground font-medium">Sync in progress…</span>
            ) : (
              <>
                Last full sync: <span className="font-medium">{lastSynced}</span>
                {syncState?.last_count != null ? ` · ${syncState.last_count} listing(s)` : ''}
                {syncState?.last_status ? ` · ${syncState.last_status}` : ''}
              </>
            )}
          </p>
        </div>
        <Button onClick={handleSync} disabled={isRunning}>
          <RefreshCw className={cn('mr-2 h-4 w-4', isRunning && 'animate-spin')} />
          {isRunning ? 'Syncing...' : 'Sync now'}
        </Button>
      </div>
    </div>
  );
};

export const SettingsPage = () => {
  usePageTitle('Settings');
  const { data: settings = [], isLoading } = useSettings();
  const bulkUpdateMutation = useBulkUpdateSettings();
  const { data: encryptionStatus } = useEncryptionStatus();
  // Retry candidates come from the dynamic outcome vocabulary (is_retryable).
  const { data: outcomeDefinitions } = useEffectiveOutcomes();
  const retryableOutcomeCandidates =
    outcomeDefinitions && outcomeDefinitions.length > 0
      ? outcomeDefinitions.filter((d) => d.is_retryable).map((d) => d.key)
      : RETRYABLE_OUTCOME_FALLBACK;
  const { toast } = useToast();

  const {
    dealerKit: featureDealerKit,
    paymentLinks: featurePaymentLinks,
    digitalSign: featureDigitalSign,
  } = useFeaturesStore();

  const [editedValues, setEditedValues] = useState<Record<string, string | null>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('');

  // Get current email provider value
  const emailProvider = editedValues['EMAIL_PROVIDER'] || '';

  // Group settings by category
  const settingsByCategory = useMemo(() => {
    return settings.reduce<SettingsByCategory>((acc, setting) => {
      const category = setting.category || 'general';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(setting);
      return acc;
    }, {});
  }, [settings]);

  // Categories gated behind a feature flag — hidden when the feature is off.
  const isCategoryVisible = (category: string): boolean => {
    if (category === SettingCategory.DealerKit) return featureDealerKit;
    if (category === SettingCategory.Payments) return featurePaymentLinks;
    if (category === SettingCategory.DigitalSign) return featureDigitalSign;
    return true;
  };

  // Both Twilio and Telnyx credential sections are always visible — the
  // active call provider is now picked per-configuration, not globally here.
  const categories = useMemo(
    () => Object.keys(settingsByCategory).filter(isCategoryVisible).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingsByCategory, featureDealerKit, featurePaymentLinks, featureDigitalSign],
  );

  // Set initial active category, and reset if the current one is no longer
  // visible (e.g. its feature flag loaded as disabled after mount).
  useEffect(() => {
    if (categories.length > 0 && !categories.includes(activeCategory)) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  // Initialize edited values from settings
  useEffect(() => {
    const values: Record<string, string | null> = {};
    settings.forEach((setting) => {
      values[setting.key] = setting.value;
    });
    setEditedValues(values);
  }, [settings]);

  const handleValueChange = (key: string, value: string | null) => {
    setEditedValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const hasChanges = useMemo(() => {
    return settings.some((setting) => {
      // For secrets, only count as changed if user entered a value
      if (setting.is_secret) {
        return !!editedValues[setting.key];
      }
      return editedValues[setting.key] !== setting.value;
    });
  }, [settings, editedValues]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const changedSettings = settings
        .filter((setting) => {
          // For secrets, only include if user entered a new value
          if (setting.is_secret) {
            return !!editedValues[setting.key];
          }
          return editedValues[setting.key] !== setting.value;
        })
        .map((setting) => ({
          key: setting.key,
          value: editedValues[setting.key] ?? null,
        }));

      if (changedSettings.length === 0) {
        toast({
          variant: 'default',
          title: 'No changes',
          description: 'No settings were modified.',
        });
        return;
      }

      await bulkUpdateMutation.mutateAsync({ settings: changedSettings });

      toast({
        variant: 'success',
        title: 'Settings saved',
        description: `${changedSettings.length} setting(s) updated successfully.`,
      });

      // Clear secret fields after save
      const clearedValues = { ...editedValues };
      settings.forEach((setting) => {
        if (setting.is_secret) {
          clearedValues[setting.key] = null;
        }
      });
      setEditedValues(clearedValues);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to save settings',
        description: extractErrorMessage(error),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    const values: Record<string, string | null> = {};
    settings.forEach((setting) => {
      values[setting.key] = setting.value;
    });
    setEditedValues(values);
  };

  // Check if a setting should be visible based on email provider
  const isSettingVisible = (setting: Setting): boolean => {
    const provider = getEmailProviderForKey(setting.key);
    if (provider) {
      return emailProvider === provider;
    }
    return true;
  };

  const renderSettingInput = (setting: Setting) => {
    const value = editedValues[setting.key] ?? '';

    // Sales Console — visual editors for the JSON-backed settings
    if (setting.key === 'CONTACT_TAG_DEFINITIONS') {
      return (
        <TagDefinitionsEditor value={value} onChange={(v) => handleValueChange(setting.key, v)} />
      );
    }
    if (setting.key === 'SALES_CONSOLE_DEFAULT_FILTERS') {
      return (
        <DefaultFiltersEditor value={value} onChange={(v) => handleValueChange(setting.key, v)} />
      );
    }

    // Campaign default outcome source — RAW vs REVIEWED
    if (setting.key === 'CAMPAIGN_DEFAULT_OUTCOME_SOURCE') {
      return (
        <FormSelect
          value={value || 'REVIEWED'}
          onChange={(e) => handleValueChange(setting.key, e.target.value)}
        >
          <option value="REVIEWED">REVIEWED — post-call review verdict (recommended)</option>
          <option value="RAW">RAW — outcome recorded at call-end</option>
        </FormSelect>
      );
    }

    // Campaign default retryable outcomes — checkbox list stored as CSV
    if (setting.key === 'CAMPAIGN_DEFAULT_RETRYABLE_OUTCOMES') {
      const selected = new Set(
        (value || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      );
      const toggle = (outcome: string) => {
        const next = new Set(selected);
        if (next.has(outcome)) next.delete(outcome);
        else next.add(outcome);
        handleValueChange(setting.key, Array.from(next).join(','));
      };
      return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {retryableOutcomeCandidates.map((outcome) => (
            <label key={outcome} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.has(outcome)}
                onChange={() => toggle(outcome)}
              />
              {outcome}
            </label>
          ))}
        </div>
      );
    }

    // Email provider dropdown
    if (setting.key === 'EMAIL_PROVIDER') {
      return (
        <FormSelect value={value} onChange={(e) => handleValueChange(setting.key, e.target.value)}>
          <option value="">Select provider...</option>
          <option value="resend">Resend</option>
          <option value="nodemailer">Nodemailer (SMTP)</option>
          <option value="sendgrid">SendGrid</option>
          <option value="azure">Azure (Microsoft Graph)</option>
        </FormSelect>
      );
    }

    // Boolean settings — derived from registry
    const registryEntry = CONFIG_REGISTRY[setting.key as ConfigKey];
    const isBooleanSetting = registryEntry?.type === ConfigType.Boolean;

    if (isBooleanSetting) {
      return (
        <FormSelect value={value} onChange={(e) => handleValueChange(setting.key, e.target.value)}>
          <option value="">Select...</option>
          <option value="false">Disabled</option>
          <option value="true">Enabled</option>
        </FormSelect>
      );
    }

    // Secret fields - password input with has_value indicator
    if (setting.is_secret) {
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            {setting.has_value ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                <Check className="h-3 w-3" />
                Configured
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                <X className="h-3 w-3" />
                Not set
              </span>
            )}
          </div>
          <FormInput
            id={setting.key}
            name={`setting-${setting.key}-${Date.now()}`}
            type="password"
            value={value}
            onChange={(e) => handleValueChange(setting.key, e.target.value || null)}
            placeholder="Leave empty to keep current value"
            autoComplete="new-password"
            data-lpignore="true"
            data-form-type="other"
          />
        </div>
      );
    }

    // Color picker
    if (registryEntry?.type === ConfigType.Color) {
      return (
        <div className="flex items-center gap-3">
          <input
            type="color"
            id={setting.key}
            value={value || '#000000'}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            className="h-10 w-14 cursor-pointer rounded border p-1"
          />
          <FormInput
            name={`setting-${setting.key}-hex`}
            value={value}
            onChange={(e) => handleValueChange(setting.key, e.target.value || null)}
            placeholder="#000000"
            className="w-32 font-mono"
            autoComplete="off"
            data-lpignore="true"
            data-form-type="other"
          />
        </div>
      );
    }

    // Regular text input
    return (
      <FormInput
        id={setting.key}
        name={`setting-${setting.key}`}
        value={value}
        onChange={(e) => handleValueChange(setting.key, e.target.value || null)}
        placeholder={`Enter ${setting.key.toLowerCase().replace(/_/g, ' ')}`}
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
      />
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Configuration</p>
          <h1 className="text-3xl font-bold tracking-tight">Platform Settings</h1>
          <p className="text-muted-foreground max-w-3xl">
            Manage platform-wide settings including branding, email configuration and more.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} disabled={!hasChanges || isSaving}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      {encryptionStatus?.configured === false && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              Encryption key not configured
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <code className="font-mono">SETTINGS_ENCRYPTION_KEY</code> is not set to a valid
              64-character hex value. Saved secrets are encrypted with an insecure dev key. Set a
              proper 64-character hex <code className="font-mono">SETTINGS_ENCRYPTION_KEY</code> in
              the backend environment to protect stored credentials.
            </p>
          </div>
        </div>
      )}

      {categories.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">
              No settings found. Settings will be auto-seeded on backend startup.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex gap-6">
          {/* Category tabs - vertical sidebar */}
          <div className="w-48 shrink-0 space-y-1">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={cn(
                  'w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors',
                  activeCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground dark:text-white/70 dark:hover:text-white',
                )}
              >
                {categoryLabels[category] || category}
              </button>
            ))}
          </div>

          {/* Settings content */}
          <div className="flex-1 space-y-6">
            {activeCategory === SettingCategory.Email && <EmailConnectorsPanel />}
            {activeCategory &&
              isCategoryVisible(activeCategory) &&
              settingsByCategory[activeCategory] && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {activeCategory === SettingCategory.Email
                        ? 'Other Email Settings'
                        : categoryLabels[activeCategory] || activeCategory}
                    </CardTitle>
                    <CardDescription>
                      {activeCategory === SettingCategory.Email
                        ? 'Global email settings that apply across all connectors.'
                        : categoryDescriptions[activeCategory] ||
                          `Manage ${activeCategory} settings.`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {settingsByCategory[activeCategory].filter(isSettingVisible).map((setting) => (
                      <FormField
                        key={setting.key}
                        label={setting.label}
                        htmlFor={setting.key}
                        description={setting.description || undefined}
                      >
                        {renderSettingInput(setting)}
                      </FormField>
                    ))}
                    {activeCategory === SettingCategory.DealerKit && <DealerKitStockSyncPanel />}
                  </CardContent>
                </Card>
              )}
          </div>
        </div>
      )}
    </div>
  );
};
